import { SelectChangeEvent } from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import TranslationField from "../../../type/translationField";
import LanguagePoint from "../../Global/LanguagePoint";
import SelectLanguage from "../../Global/SelectLanguage";
import Warning from "../../ErrorAndAlert/Warning";
import { ThemeProvider } from "@emotion/react";
import { theme } from "../../Register/AddRegister/FormTheme";
import file from "../../../type/file";
import DragDropField from "../../Global/DragDropField";
import OldFile from "../../Global/OldFile";
import Status from "../../Global/Status";
import {
  defaultLang,
  initialTranslationField,
  secondLang,
} from "../../../utils";

const AddBotanicalFamilyForm: React.FC<{
  send: (data: any) => void;
  sendResponse: any;
  title: string;
  link: string;
  getInfo?: any;
  textButton: string;
}> = ({ send, sendResponse, title, link, getInfo, textButton }) => {
  const { t } = useTranslation();

  const [lang, setLang] = useState<string>(defaultLang);
  const [name, setName] = useState<TranslationField>(initialTranslationField);
  const [description, setDescription] = useState<TranslationField>(
    initialTranslationField
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [active, isActive] = useState<boolean>(true);
  const [dirty, isDirty] = useState<boolean>(true);
  const [showError, isShowError] = useState<boolean>(false);
  const [featureIcon, setFeatureIcon] = useState<file[]>([]);
  const [oldFeatureIcon, setOldFeatureIcon] = useState<string>("");
  const history = useHistory();

  const handleLangChange = (event: SelectChangeEvent) => {
    setLang(event.target.value.toLowerCase());
  };

  useEffect(() => {
    if (sendResponse) {
      toast.success(
        `${getInfo
          ? `${t("Message.The record updated successfully")}`
          : `${t("Message.The record added successfully")}`
        }`
      );
      history.replace(link);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendResponse]);

  useEffect(() => {
    if (getInfo) {
      setLoading(false);
      setName(getInfo.data.name_transes);
      setDescription(getInfo.data.description_transes);
      if (title === t("Incidents.Incident")) {
        setOldFeatureIcon(getInfo.data.featured_icon);
      }

      isActive(
        getInfo.data.is_active_transes?.[secondLang] === "Active" ? true : false
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getInfo]);

  const nameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedName = { ...name };
    updatedName[lang] = event.target.value;
    setName(updatedName);
  };

  const descChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const updatedDescription = { ...description };
    updatedDescription[lang] = event.target.value;
    setDescription(updatedDescription);
  };

  const insertFile = (newFile: file[]) => {
    if (!newFile[0].type.includes("image")) {
      toast.error(`${t("Message.The supported formats are “jpg/jpeg, png”")}`);
    } else if (newFile[0].size > 5000000) {
      toast.error(`${t("Message.Maximum file size is 5 MB")}`);
    } else {
      setFeatureIcon([newFile[0]]);
      setOldFeatureIcon("");
    }
  };

  const removeFile = (index: number) => {
    setFeatureIcon([]);
  };

  const removeOldFeatureIcon = (index: number) => {
    setOldFeatureIcon("");
  };

  const validateDefaultLanguage = () => {
    if (
      (name[secondLang].trim() !== "" && name[defaultLang].trim() === "") ||
      (description[secondLang].trim() !== "" &&
        description[defaultLang].trim() === "")
    ) {
      toast.error(
        `${t(
          "Message.While other languages have a translation, the default language cannot be empty."
        )}`
      );
      return undefined;
    } else {
      return name[defaultLang];
    }
  };

  const addIncident = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const nameES = validateDefaultLanguage();
    if (nameES) {
      isDirty(false);
      if (title === t("Incidents.Incident")) {
        const formData = new FormData();
        formData.append("name_transes ", JSON.stringify(name));
        formData.append("description_transes", JSON.stringify(description));
        formData.append("is_active", active ? "True" : "False");
        if (!oldFeatureIcon && featureIcon.length !== 0) {
          formData.append("featured_icon", featureIcon[0]?.data);
        } else if (!oldFeatureIcon && featureIcon.length === 0) {
          formData.append("featured_icon", "");
        }
        send(formData);
      } else {
        send({
          name_transes: name,
          description_transes: description,
          is_active: active,
        });
      }
    }
  };

  return (
    <Fragment>
      {loading && getInfo ? (
        <p className="text-center text-2xl font-medium text-default-text">
          {t("Message.Loading")}
        </p>
      ) : (
        <form onSubmit={addIncident}>
          <ThemeProvider theme={theme}>
            <p className="text-sm text-ph-dark-text mb-[6px]">
              {t("AddPlanting.Language")}
            </p>
            <SelectLanguage
              handleLangChange={handleLangChange}
              lang={lang.toUpperCase()}
            />
          </ThemeProvider>
          <div className="flex flex-wrap flex-row items-start">
            <div className="w-full md:w-[50%]">
              <p className="text-sm text-ph-dark-text mb-[6px] mt-7">
                {title} * <LanguagePoint lang={lang.toUpperCase()} />
              </p>
              <input
                onChange={nameChange}
                value={name[lang] || ""}
                type="text"
                className="input-field pl-4 py-3 text-sm text-default-text placeholder-ph-light-gray"
                placeholder={title}
              />
              {showError && !name[defaultLang] && (
                <Fragment>
                  <p className="error-text">{t("Message.Required field")}</p>
                </Fragment>
              )}
            </div>
            {!title.includes("Botanical") && (
              <div className="md:pl-4 mt-7 flex">
                <Status
                  active={active}
                  isActive={(value) => {
                    isActive(value);
                  }}
                />
              </div>
            )}
          </div>
          <p className="text-sm text-ph-dark-text mt-7 mb-[6px]">
            {t("Tree Species.Description")}{" "}
            <LanguagePoint lang={lang.toUpperCase()} />
          </p>
          <textarea
            onChange={descChange}
            value={description[lang] || ""}
            rows={7}
            className="input-field resize-none text-sm text-default-text pl-4 py-[10px] placeholder-ph-light-gray"
            placeholder={t("Tree Species.Description") + ""}
          />

          {title === t("Incidents.Incident") && (
            <Fragment>
              <p className="text-sm text-default-text mt-7 mb-[6px]">
                {t("Tree Species.Featured icon")}
              </p>
              <DragDropField
                files={featureIcon}
                format="png (128 x 128 pixels) or SVG"
                insertFile={insertFile}
                removeFile={removeFile}
              />

              {oldFeatureIcon && (
                <OldFile
                  address={oldFeatureIcon}
                  id={0}
                  delete={true}
                  removeOldFile={removeOldFeatureIcon}
                />
              )}
            </Fragment>
          )}

          <button
            onClick={() => {
              isShowError(true);
            }}
            type="submit"
            className="green-btn text-sm  px-6 py-2 mt-5 float-right font-medium"
          >
            {textButton}
          </button>
        </form>
      )}
      <Warning when={dirty} onCancel={() => false} onOK={() => true} />
    </Fragment>
  );
};

export default AddBotanicalFamilyForm;
